import { Col, Form, Row, DatePicker, Spin, Select, Input, Empty, Button, message, Checkbox } from 'antd'
import locale from 'antd/es/date-picker/locale/es_ES';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { handlers } from '../../helpers/helpers';
import { getCookie, listenerCookie } from '../../services/cookies';
import { getExamsByPatient, summaryLaboratory, traceabilityReport } from '../../services/exam';
import { getDepartments, getExams, getExamTypes } from '../../services/general';
import { normalizeDocumentNumber } from "../../utils/inputValidations";
import countrySettings from './countrySettings.json';
import ExamTabs from './components/ExamTabs';
import { useTranslation } from 'react-i18next';
import UnifiedSearchFilters from '../../components/UnifiedSearchFilters/UnifiedSearchFilters';
import { HasAccess } from "@permify/react-role";
import { exportLaboratorySummaryToExcel, exportTraceabilitySamplesToExcel } from '../../utils/excelUtils';

const defaultPage = { pending: 1, reevaluation: 1, treated: 1, };
const defaultTab = { data: [], pagination: {} };

const alignment = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const ExamPage = () => {
  const [filters, setFilters] = useState({
    departments: [],
    exams: [],
    types: [],
  });
  const [loading, setLoading] = useState({
    departments: false,
    exams: false,
    types: false,
    tabs: false,
    pending: false,
    reevaluation: false,
    treated: false,
    summaryLaboratory: false,
    traceabilityReport: false,
  });
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [tabsExams, setTabsExams] = useState({
    pending: { ...defaultTab },
    reevaluation: { ...defaultTab },
    treated: { ...defaultTab },
  });
  const [queryParams, setQueryParams] = useState(() => {
    const query = Object.fromEntries(new URLSearchParams(window.location.search));
    query.branch_office_id = getCookie('branch_office_id');
    if (!query.date_start) query.date_start = moment().format('YYYY-MM-DD');
    if (!query.date_end) query.date_end = moment().format('YYYY-MM-DD');
    return query;
  });
  const roles = getCookie('roles') || [];
  const countryCode = getCookie('country_code')
  const [allBranchOffices, setAllBranchOffices] = useState(
    countrySettings?.useGranularSearchFilters[countryCode] ? (roles && roles.includes('direccion_medica')) : (roles && roles.includes('equipo_medico') && roles.includes('administracion'))
  );

  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const fetchService = useCallback(async (endpoint, type) => {
    setLoading(loading => ({ ...loading, [type]: true }));
    try {
      const response = await endpoint();
      setFilters(filters => ({ ...filters, [type]: response }));
    } catch (error) {
      console.error('Error, ', error);
    }
    setLoading(loading => ({ ...loading, [type]: false }));
  }, []);

  const fetchTabsExams = useCallback(async (params) => {
    setLoading(loading => ({ ...loading, [params.tab]: true }));
    try {
      if ((params.date_start && params.date_end) || params.department_id || params.exam_id || params.exam_type || params.query) {
        if (params.query) {
          params.query = normalizeDocumentNumber(params.query);
        }
        const response = await getExamsByPatient(params);
        const responseWithKeys = {
          ...response,
          data: response.data.map((item, index) => ({ ...item, key: index })),
        };
        setTabsExams(exams => ({ ...exams, [params.tab]: responseWithKeys }));
      } else {
        setTabsExams({
          pending: { ...defaultTab },
          reevaluation: { ...defaultTab },
          treated: { ...defaultTab },
        });
      }
    } catch (error) {
      console.error('Error, ', error);
    }
    setLoading(loading => ({ ...loading, [params.tab]: false }));
  }, []);

  const fetchExams = async (name, id = null) => {
    setLoading(loading => ({ ...loading, exams: true }));
    try {
      let response = [];
      if (name && name !== '') {
        response = (await getExams({ exam_name: name })).data;
      } else if (id) {
        response = (await getExams({ exam_id: id })).data;
      }
      setFilters(filters => ({ ...filters, exams: response }));
    } catch (error) {
      console.error('Error, ', error);
    }
    setLoading(loading => ({ ...loading, exams: false }));
  };

  const debounceExams = debounce(async (name) => {
    fetchExams(name, null);
  }, 500);

  useEffect(() => {
    fetchService(getDepartments, 'departments');
    fetchService(getExamTypes, 'types');
    if (queryParams.exam_id) {
      fetchExams(null, queryParams.exam_id);
    }
  }, [fetchService, queryParams.exam_id]);

  useEffect(() => {
    const formParams = {
      ...queryParams,
      date_range: [moment(queryParams.date_start || undefined), moment(queryParams.date_end || undefined)]
    };
    form.setFieldsValue(formParams);
  }, []);

  useEffect(() => {
    fetchTabsExams({
      ...queryParams,
      tab: 'pending',
      page: currentPage.pending,
    });
  }, [fetchTabsExams, queryParams, currentPage.pending]);

  useEffect(() => {
    fetchTabsExams({
      ...queryParams,
      tab: 'reevaluation',
      page: currentPage.reevaluation,
    });
  }, [fetchTabsExams, queryParams, currentPage.reevaluation]);

  useEffect(() => {
    fetchTabsExams({
      ...queryParams,
      tab: 'treated',
      page: currentPage.treated,
    });
  }, [fetchTabsExams, queryParams, currentPage.treated]);

  useEffect(() => {
    listenerCookie(data => {
      if (data.name === 'branch_office_id') {
        setCurrentPage(defaultPage);
        setQueryParams(state => ({ ...state, branch_office_id: data.value }));
      }
    });
  }, [navigate]);

  const removeFilters = () => {
    form.resetFields();
    setCurrentPage(defaultPage);
    setQueryParams(state => ({
      branch_office_id: state.branch_office_id,
    }));
    setFilters({
      departments: [],
      exams: [],
      types: [],
    });
    navigate(`${location.pathname}`);
  };

  const onFinish = (values) => {
    const newQueryString = {};
    if (values.date_range) {
      newQueryString.date_start = values.date_range[0].format('YYYY-MM-DD');
      newQueryString.date_end = values.date_range[1].format('YYYY-MM-DD');
    } else if (values.start_date && values.end_date) {
      newQueryString.date_start = values.start_date;
      newQueryString.date_end = values.end_date;
    }
    
    Object.entries(values).forEach(([key, value]) => {
      if (value && !['date_range', 'dates', 'start_date', 'end_date'].includes(key)) newQueryString[key] = value;
    });

    if (values.allBranchOffices) {
      delete newQueryString.branch_office_id;
    }

    setCurrentPage(defaultPage);
    setQueryParams(state => {
      const nextParams = {
        ...newQueryString,
      };
      if (values.allBranchOffices) {
        delete nextParams.branch_office_id;
      } else if (!nextParams.branch_office_id) {
        nextParams.branch_office_id = getCookie('branch_office_id');
      }
      return nextParams;
    });
    handlers.replaceUrlQueryString(navigate, newQueryString);
  };

  const getLaboratorySummary = async () => {
    setLoading(loading => ({ ...loading, summaryLaboratory: true }));
    try {
      const response = await summaryLaboratory({
        branch_office_id: getCookie('branch_office_id'),
      });
      if (response?.patients) {
        await exportLaboratorySummaryToExcel(response, t);
      } else {
        console.error('Error', response);
        message.error({
          content: 'Error al descargar resumen',
          style: {
            marginTop: '30vh'
          }
        });
      }
    } catch (error) {
      console.error('Error', error);
      message.error({
        content: 'Error al descargar resumen',
        style: {
          marginTop: '30vh'
        }
      });
    }
    setLoading(loading => ({ ...loading, summaryLaboratory: false }));
  }

  const getTraceabilityReport = async () => {
    setLoading((loading) => ({ ...loading, traceabilityReport: true }));
    try {
      const formValues = form.getFieldsValue();

      let startDate = queryParams.date_start;
      let endDate = queryParams.date_end;

      if (formValues.date_range && formValues.date_range[0] && formValues.date_range[1]) {
        startDate = formValues.date_range[0].format('YYYY-MM-DD');
        endDate = formValues.date_range[1].format('YYYY-MM-DD');
      }
      if (!startDate || !endDate) {
        message.warning({
          content: 'Por favor selecciona un rango de fechas',
          style: {
            marginTop: '30vh',
          },
        });
        setLoading((loading) => ({ ...loading, traceabilityReport: false }));
        return;
      }

      const response = await traceabilityReport({
        startDate,
        endDate,
      });
      if (response?.length > 0) {
        await exportTraceabilitySamplesToExcel(response, 'seguimiento_muestras');
        message.success({
          content: 'Reporte descargado exitosamente',
          style: {
            marginTop: '30vh',
          },
        });
      } else {
        message.warning({
          content: 'No hay datos disponibles para el rango de fechas seleccionado',
          style: {
            marginTop: '30vh',
          },
        });
      }
    } catch (error) {
      console.error('Error', error);
      message.error({
        content: 'Error al descargar el reporte de seguimiento',
        style: {
          marginTop: '30vh',
        },
      });
    }
    setLoading((loading) => ({ ...loading, traceabilityReport: false }));
  };

  const renderDepartmentFilter = (isUnified) => (
    <Col xl={6} lg={isUnified ? 8 : 6} md={isUnified ? 12 : undefined} xs={24} key="department_id">
      <Spin spinning={loading.departments} className={isUnified ? '' : 'mt-10'}>
        <Form.Item name="department_id" label="Departamento">
          <Select className="cmt-select" placeholder="Todos">
            <Select.Option key={0} value={0}>Todos</Select.Option>
            {filters.departments.map((item, index) => (
              <Select.Option key={index + 1} value={item.id}>{item.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Spin>
    </Col>
  );

  const renderExamFilter = (isUnified) => (
    <Col xl={6} lg={isUnified ? 8 : 6} md={isUnified ? 12 : undefined} xs={24} key="exam_id">
      <Spin spinning={loading.exams} className={isUnified ? '' : 'mt-10'}>
        <Form.Item name="exam_id" label="Examen">
          <Select
            className="cmt-select"
            placeholder="Todos"
            showSearch
            showArrow={false}
            filterOption={false}
            allowClear
            onSearch={debounceExams}
            onClear={() => setFilters((filters) => ({ ...filters, exams: [] }))}
            notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div>No hay coincidencias</div>} />}
          >
            {filters.exams.map((item) => (
              <Select.Option key={item.id} value={item.id.toString()}>{item.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Spin>
    </Col>
  );

  const renderExamTypeFilter = (isUnified) => (
    <Col xl={6} lg={isUnified ? 8 : 6} md={isUnified ? 12 : undefined} xs={24} key="exam_type">
      <Spin spinning={loading.types} className={isUnified ? '' : 'mt-10'}>
        <Form.Item name="exam_type" label="Tipo de examen">
          <Select className="cmt-select" placeholder="Todos" allowClear>
            {filters.types.map((item, index) => (
              <Select.Option key={index} value={item.id}>{item.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Spin>
    </Col>
  );

  return (
    <Row justify="center" className="exam-page">
      <Col lg={24} xs={24}>
        <Row justify="center" gutter={[0, 50]}>
          <Col lg={22} xs={22} className="form-wrapper">
            <Row>
              <Col lg={13} xs={24} className="mt-15 mb-lg-0 mb-md-10 mb-sm-10 mb-xs-10">
                <h1 className="mb-0">Exámenes</h1>
              </Col>
              <Col lg={6} xs={24} className="mt-15 mb-lg-0 mb-md-20 mb-sm-20 mb-xs-20 pr-lg-5">
                {countrySettings?.downloadTraceabilityReport[countryCode] &&
                  <Button onClick={getTraceabilityReport} className="cmt-button primary outline" type="primary" loading={loading.traceabilityReport}>
                    Seguimiento de muestras
                  </Button>
                }
              </Col>
              <Col lg={5} xs={24} className="mt-15 mb-lg-0 mb-md-20 mb-sm-20 mb-xs-20 mb-20">
                <HasAccess roles={['super_usuario', 'equipo_medico']} permissions="lab-summary">
                  <Button onClick={getLaboratorySummary} className="cmt-button primary outline" type="primary" loading={loading.summaryLaboratory}>
                    Resumen de laboratorio
                  </Button>
                </HasAccess>
              </Col>
            </Row>
            {countryCode === 'PE' ? (
              <UnifiedSearchFilters
                moduleName="Exámenes"
                baseFilters={['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'workerId', 'workerName', 'reservationNumber', 'protocol', 'dateRange', 'evaluationType', 'allBranchOffices']}
                customFilters={[]}
                isCollapsible={true}
                userHasSystemWideAccess={allBranchOffices}
                onSearch={onFinish}
                onClear={removeFilters}
                loading={loading.pending || loading.reevaluation || loading.treated}
                initialValues={{
                  dates: [moment(queryParams.date_start), moment(queryParams.date_end)],
                  department_id: queryParams.department_id,
                  exam_id: queryParams.exam_id,
                  exam_type: queryParams.exam_type,
                  company_id: queryParams.company_id ? parseInt(queryParams.company_id) : undefined,
                  outsourcing_id: queryParams.outsourcing_id ? parseInt(queryParams.outsourcing_id) : undefined,
                  third_company_id: queryParams.third_company_id ? parseInt(queryParams.third_company_id) : undefined,
                  patient_document_number: queryParams.patient_document_number,
                  patient_names: queryParams.patient_names,
                  reservation_number: queryParams.reservation_number,
                  protocol_id: queryParams.protocol_id ? parseInt(queryParams.protocol_id) : undefined,
                  evaluation_types: queryParams.evaluation_types ? (Array.isArray(queryParams.evaluation_types) ? queryParams.evaluation_types : [queryParams.evaluation_types]) : undefined,
                  branch_office_id: queryParams.allBranchOffices === 'true' ? undefined : (queryParams.branch_office_id ? parseInt(queryParams.branch_office_id) : parseInt(getCookie('branch_office_id'))),
                  allBranchOffices: queryParams.allBranchOffices === 'true'
                }}
                customNodes={[
                  renderDepartmentFilter(true),
                  renderExamFilter(true),
                  renderExamTypeFilter(true)
                ]}
              />
            ) : (
              <Form form={form} onFinish={onFinish} size="large" layout="vertical">
                <Row>
                  <Col lg={24} xs={24}>
                    <Row justify="space-between" gutter={15}>
                      <Col lg={6} xs={24}>
                        <Form.Item name="date_range" label="Fecha">
                          <DatePicker.RangePicker
                            locale={locale}
                            format="DD-MM-YYYY"
                            inputReadOnly
                            placeholder={['Desde', 'Hasta']}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      {renderDepartmentFilter(false)}
                      {renderExamFilter(false)}
                      {renderExamTypeFilter(false)}
                    </Row>
                  </Col>
                  <Col lg={24} xs={24} className="mb-lg-0 mb-md-20 mb-sm-20 mb-xs-20">
                    <Row gutter={15}>
                      <Col lg={12} xs={24} className="mt-10 mb-lg-0 mb-md-30 mb-sm-30 mb-xs-30">
                        <Form.Item name="query" className="mb-0">
                          <Input
                            className="cmt-input"
                            placeholder={t('examPage.list.queryPlaceholder')}
                            prefix={<i className="icon-system-search" />}
                          />
                        </Form.Item>
                      </Col>
                      <Col style={alignment}>
                        <Button htmlType="submit" className="cmt-button primary" type="primary">
                          Buscar
                        </Button>
                      </Col>
                      <Col style={alignment}>
                        <Button onClick={removeFilters} className="cmt-button primary non-outline p-0" type="primary">
                          Borrar filtros
                        </Button>
                      </Col>
                      <Col lg={6} xs={24} style={alignment} className="mt-lg-0 mt-md-20 mt-sm-20 mt-xs-20">
                        {allBranchOffices && (
                          <Row justify="center" align="center">
                            <Col>
                              <Form.Item name="allBranchOffices" valuePropName="checked">
                                <Checkbox className="cmt-checkbox" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <p className="ml-10 mt-10">{t('medicalConsultations.byFilters.showAllBranchOffices')}</p>
                            </Col>
                          </Row>
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            )}
          </Col>
          <Col lg={24} xs={24} className="tabs-wrapper">
            <ExamTabs
              form={form}
              tabsExams={tabsExams}
              loading={loading}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              location={location}
              countryCode={countryCode}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

export default ExamPage;